import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/utils/api";
import { toast } from "sonner";
import { useUserSettings } from "@/contexts/UserSettingsContext";
import { CURRENCIES, formatCurrencyOption } from "@/utils/currencies";
import { SettingsPreviewPanel } from "@/components/shared";
import ActiveSessionsPanel from "@/components/ActiveSessionsPanel";
import AssistantConnectSection from "@/components/AssistantConnectSection";
import { LinkedAccountsCard } from "@/components/auth/LinkedAccountsCard";

const CRUISE_TYPES = [
  { key: 'river', label: 'River Cruises' },
  { key: 'luxury', label: 'Luxury or Expedition' },
  { key: 'island', label: 'Island/Short Cruises' },
  { key: 'repositioning', label: 'Repositioning Cruises' }
];

const UserSettingsDialog = ({ open, onOpenChange, travelers = [], onGettingStartedChange }) => {
  const { refetchSettings } = useUserSettings();
  const [loading, setLoading] = useState(false);
  const [alternateEmails, setAlternateEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [gettingStartedHidden, setGettingStartedHidden] = useState(false);
  const [settings, setSettings] = useState({
    home_currency: "USD",
    selected_traveler_id: "",
    // User Preferences
    distance_unit: "miles", // "miles" or "kilometers"
    time_format: "12h", // "12h" or "24h"
    date_format: "MM/DD/YYYY", // "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"
    theme: "light", // "light" or "dark"
    // SMS reminder preferences
    timezone: "UTC",
    sms_reminders_enabled: true,
    sms_reminder_default_minutes: 30,
    sms_night_before_enabled: true,
    sms_night_before_local_hour: 19,
    // River Cruises
    river_transportation_percent: 30.0,
    river_accommodation_percent: 50.0,
    river_meals_entertainment_percent: 20.0,
    // Luxury or Expedition
    luxury_transportation_percent: 25.0,
    luxury_accommodation_percent: 55.0,
    luxury_meals_entertainment_percent: 20.0,
    // Island/Short
    island_transportation_percent: 35.0,
    island_accommodation_percent: 45.0,
    island_meals_entertainment_percent: 20.0,
    // Repositioning
    repositioning_transportation_percent: 40.0,
    repositioning_accommodation_percent: 40.0,
    repositioning_meals_entertainment_percent: 20.0
  });

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchAlternateEmails();
      fetchOnboarding();
    }
  }, [open]);

  const fetchOnboarding = async () => {
    try {
      const response = await api.get(`/user/onboarding`);
      setGettingStartedHidden(!!response.data.getting_started_hidden);
    } catch (error) {
      // Non-fatal
    }
  };

  const handleToggleGettingStarted = async (show) => {
    const hidden = !show;
    setGettingStartedHidden(hidden);
    try {
      await api.patch(`/user/onboarding`, { getting_started_hidden: hidden });
      onGettingStartedChange?.(hidden);
      toast.success(show ? "Getting Started is now shown on your dashboard" : "Getting Started hidden");
    } catch (error) {
      toast.error("Failed to update Getting Started preference");
    }
  };

  const fetchAlternateEmails = async () => {
    try {
      const response = await api.get(`/user/settings/alternate-emails`);
      setAlternateEmails(response.data.alternate_emails || []);
    } catch (error) {
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setAddingEmail(true);
    try {
      await api.post(
        `/user/settings/alternate-emails`,
        { email: newEmail.toLowerCase() }
      );
      
      toast.success("Email added successfully!");
      setNewEmail("");
      await fetchAlternateEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add email");
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    try {
      await api.delete(`/user/settings/alternate-emails/${encodeURIComponent(email)}`);
      
      toast.success("Email removed");
      await fetchAlternateEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove email");
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/user-settings`);
      setSettings(response.data);
      
      // Cache settings in localStorage for utilities like dateHelpers
      try {
        localStorage.setItem('userSettings', JSON.stringify({
          date_format: response.data.date_format,
          time_format: response.data.time_format,
          distance_unit: response.data.distance_unit,
          home_currency: response.data.home_currency
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate percentages total 100 for each cruise type
    const cruiseTypes = ['river', 'luxury', 'island', 'repositioning'];
    
    for (const type of cruiseTypes) {
      const total = 
        parseFloat(settings[`${type}_transportation_percent`]) +
        parseFloat(settings[`${type}_accommodation_percent`]) +
        parseFloat(settings[`${type}_meals_entertainment_percent`]);
      
      if (Math.abs(total - 100) > 0.01) {
        const cruiseLabel = CRUISE_TYPES.find(ct => ct.key === type)?.label || type;
        toast.error(`${cruiseLabel} percentages must total 100%. Current total: ${total.toFixed(1)}%`);
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        home_currency: settings.home_currency,
        selected_traveler_id: settings.selected_traveler_id || null,
        // Display preferences
        distance_unit: settings.distance_unit || 'miles',
        time_format: settings.time_format || '12h',
        date_format: settings.date_format || 'MM/DD/YYYY',
        theme: settings.theme || 'light',
        // SMS reminder preferences
        timezone: settings.timezone || 'UTC',
        sms_reminders_enabled: !!settings.sms_reminders_enabled,
        sms_reminder_default_minutes: parseInt(settings.sms_reminder_default_minutes ?? 30, 10),
        sms_night_before_enabled: !!settings.sms_night_before_enabled,
        sms_night_before_local_hour: parseInt(settings.sms_night_before_local_hour ?? 19, 10),
      };
      
      // Add all cruise type percentages
      cruiseTypes.forEach(type => {
        payload[`${type}_transportation_percent`] = parseFloat(settings[`${type}_transportation_percent`]);
        payload[`${type}_accommodation_percent`] = parseFloat(settings[`${type}_accommodation_percent`]);
        payload[`${type}_meals_entertainment_percent`] = parseFloat(settings[`${type}_meals_entertainment_percent`]);
      });
      
      await api.put(`/user-settings`, payload);
      
      // Cache settings in localStorage for utilities like dateHelpers
      try {
        localStorage.setItem('userSettings', JSON.stringify({
          date_format: settings.date_format,
          time_format: settings.time_format,
          distance_unit: settings.distance_unit,
          home_currency: settings.home_currency
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
      
      // Refresh context so all components get the new settings
      await refetchSettings();
      
      toast.success("Settings saved successfully!");
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (cruiseType) => {
    return (
      parseFloat(settings[`${cruiseType}_transportation_percent`] || 0) +
      parseFloat(settings[`${cruiseType}_accommodation_percent`] || 0) +
      parseFloat(settings[`${cruiseType}_meals_entertainment_percent`] || 0)
    ).toFixed(1);
  };

  const areAllTotalsValid = () => {
    return CRUISE_TYPES.every(cruiseType => {
      const total = parseFloat(calculateTotal(cruiseType.key));
      return Math.abs(total - 100) < 0.01;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Settings</DialogTitle>
          <DialogDescription>
            Configure your application preferences and defaults.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading settings...</div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Home Currency */}
            <div className="space-y-2">
              <Label htmlFor="home_currency">Home Currency</Label>
              <Select 
                value={settings.home_currency} 
                onValueChange={(value) => setSettings({ ...settings, home_currency: value })}
              >
                <SelectTrigger id="home_currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="max-h-[300px]">
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {formatCurrencyOption(currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Your preferred currency for budget calculations and reports
              </p>
            </div>

            {/* Selected Traveler */}
            <div className="space-y-2">
              <Label htmlFor="selected_traveler">Your Traveler Profile</Label>
              <Select 
                value={settings.selected_traveler_id || "none"} 
                onValueChange={(value) => setSettings({ 
                  ...settings, 
                  selected_traveler_id: value === "none" ? null : value 
                })}
              >
                <SelectTrigger id="selected_traveler">
                  <SelectValue placeholder="Select which traveler is you" />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="none">None selected</SelectItem>
                  {travelers.map((traveler) => (
                    <SelectItem key={traveler.id} value={traveler.id}>
                      {traveler.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Select which traveler profile represents you
              </p>
            </div>

            {/* Display Preferences Section */}
            <div className="pt-4 border-t space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Display Preferences</h3>

              {/* Live preview — shows how every preference combination will
                  render in the app, updating synchronously as the user
                  toggles selects. Critical visual safety net before Save. */}
              <SettingsPreviewPanel settings={settings} />
              
              {/* Distance Unit */}
              <div className="space-y-2">
                <Label htmlFor="distance_unit">Distance Unit</Label>
                <Select
                  value={settings.distance_unit || "miles"}
                  onValueChange={(value) => setSettings({ ...settings, distance_unit: value })}
                >
                  <SelectTrigger id="distance_unit">
                    <SelectValue placeholder="Select distance unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="miles">Miles (mi)</SelectItem>
                    <SelectItem value="kilometers">Kilometers (km)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Choose how distances are displayed for flights</p>
              </div>

              {/* Time Format */}
              <div className="space-y-2">
                <Label htmlFor="time_format">Time Format</Label>
                <Select
                  value={settings.time_format || "12h"}
                  onValueChange={(value) => setSettings({ ...settings, time_format: value })}
                >
                  <SelectTrigger id="time_format">
                    <SelectValue placeholder="Select time format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12h">12-hour (2:30 PM)</SelectItem>
                    <SelectItem value="24h">24-hour (14:30)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Choose your preferred time display format</p>
              </div>

              {/* Date Format */}
              <div className="space-y-2">
                <Label htmlFor="date_format">Date Format</Label>
                <Select
                  value={settings.date_format || "MM/DD/YYYY"}
                  onValueChange={(value) => setSettings({ ...settings, date_format: value })}
                >
                  <SelectTrigger id="date_format">
                    <SelectValue placeholder="Select date format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/25/2024)</SelectItem>
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (25/12/2024)</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-25)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Choose your preferred date display format</p>
              </div>

              {/* Theme */}
              <div className="space-y-2">
                <Label htmlFor="theme">Theme</Label>
                <Select
                  value={settings.theme || "light"}
                  onValueChange={(value) => setSettings({ ...settings, theme: value })}
                >
                  <SelectTrigger id="theme">
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System Default</SelectItem>
                    <SelectItem value="light">Light Mode</SelectItem>
                    <SelectItem value="dark">Dark Mode</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  System Default follows your device's light/dark preference
                </p>
              </div>
            </div>

            {/* Getting Started card visibility */}
            <div className="pt-4 border-t space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Getting Started</h3>
              <Label htmlFor="getting_started_toggle">Setup checklist</Label>
              <Select
                value={gettingStartedHidden ? "off" : "on"}
                onValueChange={(v) => handleToggleGettingStarted(v === "on")}
              >
                <SelectTrigger id="getting_started_toggle" data-testid="settings-getting-started-toggle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="on">Show on dashboard</SelectItem>
                  <SelectItem value="off">Hidden</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Show the setup checklist card at the top of your dashboard.
              </p>
            </div>

            {/* SMS Reminder Preferences */}
            <div className="pt-4 border-t space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">SMS Reminders</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Reminders are sent to your selected traveler profile's phone number (must have SMS consent enabled on that traveler).
              </p>

              {/* Timezone */}
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={settings.timezone || "UTC"}
                  onValueChange={(value) => setSettings({ ...settings, timezone: value })}
                >
                  <SelectTrigger id="timezone" data-testid="settings-timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[300px]">
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific (Los Angeles)</SelectItem>
                    <SelectItem value="America/Denver">Mountain (Denver)</SelectItem>
                    <SelectItem value="America/Chicago">Central (Chicago)</SelectItem>
                    <SelectItem value="America/New_York">Eastern (New York)</SelectItem>
                    <SelectItem value="America/Toronto">Eastern (Toronto)</SelectItem>
                    <SelectItem value="America/Sao_Paulo">São Paulo</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Europe/Berlin">Berlin</SelectItem>
                    <SelectItem value="Europe/Athens">Athens</SelectItem>
                    <SelectItem value="Asia/Dubai">Dubai</SelectItem>
                    <SelectItem value="Asia/Kolkata">India</SelectItem>
                    <SelectItem value="Asia/Bangkok">Bangkok</SelectItem>
                    <SelectItem value="Asia/Singapore">Singapore</SelectItem>
                    <SelectItem value="Asia/Hong_Kong">Hong Kong</SelectItem>
                    <SelectItem value="Asia/Shanghai">China</SelectItem>
                    <SelectItem value="Asia/Tokyo">Japan</SelectItem>
                    <SelectItem value="Asia/Seoul">Korea</SelectItem>
                    <SelectItem value="Australia/Sydney">Sydney</SelectItem>
                    <SelectItem value="Pacific/Auckland">Auckland</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Used to calculate the "7 PM local time" digest.</p>
              </div>

              {/* Night-before digest */}
              <div className="space-y-2">
                <Label>Night-before digest</Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={settings.sms_night_before_enabled ? "on" : "off"}
                    onValueChange={(v) => setSettings({ ...settings, sms_night_before_enabled: v === "on" })}
                  >
                    <SelectTrigger className="w-28" data-testid="settings-night-before-toggle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="on">On</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">at</span>
                  <Select
                    value={String(settings.sms_night_before_local_hour ?? 19)}
                    onValueChange={(v) => setSettings({ ...settings, sms_night_before_local_hour: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="w-28" data-testid="settings-night-before-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[240px]">
                      {Array.from({ length: 24 }, (_, h) => (
                        <SelectItem key={h} value={String(h)}>
                          {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">local time</span>
                </div>
              </div>

              {/* Day-of reminders */}
              <div className="space-y-2">
                <Label>Day-of reminders (for timed todos)</Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={settings.sms_reminders_enabled ? "on" : "off"}
                    onValueChange={(v) => setSettings({ ...settings, sms_reminders_enabled: v === "on" })}
                  >
                    <SelectTrigger className="w-28" data-testid="settings-day-of-toggle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="on">On</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">default</span>
                  <Input
                    type="number"
                    min="1"
                    max="240"
                    value={settings.sms_reminder_default_minutes ?? 30}
                    onChange={(e) => setSettings({ ...settings, sms_reminder_default_minutes: parseInt(e.target.value || 30, 10) })}
                    className="w-20"
                    data-testid="settings-default-lead"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">min before due time</span>
                </div>
                <p className="text-xs text-gray-500">Individual todos can override this lead time.</p>
              </div>
            </div>

            {/* Linked Accounts — Apple/Google/Email link status + actions */}
            <LinkedAccountsCard />

            {/* Alternate Emails */}
            <div className="space-y-2">
              <Label>Alternate Email Addresses</Label>
              <p className="text-xs text-gray-500 mb-2">
                Add email addresses you use for travel bookings. Emails forwarded from any of these addresses will be accepted for parsing.
              </p>
              
              {/* List of alternate emails */}
              {alternateEmails.length > 0 && (
                <div className="space-y-2 mb-3">
                  {alternateEmails.map((email) => (
                    <div key={email} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <span className="text-sm">{email}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveEmail(email)}
                        className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddEmail();
                    }
                  }}
                />
                <Button
                  onClick={handleAddEmail}
                  disabled={addingEmail || !newEmail.trim()}
                  size="sm"
                >
                  {addingEmail ? "Adding..." : "Add"}
                </Button>
              </div>
            </div>

            {/* Cruise Cost Breakdown */}
            <div className="space-y-4">
              <div>
                <Label>Cruise Cost Breakdown by Type</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Configure cost allocation percentages for different cruise types (each row must total 100%)
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 p-3 text-left text-sm font-medium text-gray-700">
                        Cruise Type
                      </th>
                      <th className="border border-gray-200 p-3 text-center text-sm font-medium text-gray-700">
                        Transportation %
                      </th>
                      <th className="border border-gray-200 p-3 text-center text-sm font-medium text-gray-700">
                        Accommodation %
                      </th>
                      <th className="border border-gray-200 p-3 text-center text-sm font-medium text-gray-700">
                        Meals & Entertainment %
                      </th>
                      <th className="border border-gray-200 p-3 text-center text-sm font-medium text-gray-700">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {CRUISE_TYPES.map((cruiseType) => {
                      const total = calculateTotal(cruiseType.key);
                      const isValidTotal = Math.abs(parseFloat(total) - 100) < 0.01;
                      
                      return (
                        <tr key={cruiseType.key} className="hover:bg-gray-50">
                          <td className="border border-gray-200 p-3 font-medium text-sm">
                            {cruiseType.label}
                          </td>
                          <td className="border border-gray-200 p-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={settings[`${cruiseType.key}_transportation_percent`]}
                              onChange={(e) => setSettings({ 
                                ...settings, 
                                [`${cruiseType.key}_transportation_percent`]: e.target.value 
                              })}
                              className="h-8 text-center"
                            />
                          </td>
                          <td className="border border-gray-200 p-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={settings[`${cruiseType.key}_accommodation_percent`]}
                              onChange={(e) => setSettings({ 
                                ...settings, 
                                [`${cruiseType.key}_accommodation_percent`]: e.target.value 
                              })}
                              className="h-8 text-center"
                            />
                          </td>
                          <td className="border border-gray-200 p-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={settings[`${cruiseType.key}_meals_entertainment_percent`]}
                              onChange={(e) => setSettings({ 
                                ...settings, 
                                [`${cruiseType.key}_meals_entertainment_percent`]: e.target.value 
                              })}
                              className="h-8 text-center"
                            />
                          </td>
                          <td className={`border border-gray-200 p-3 text-center font-medium ${
                            isValidTotal ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {total}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Overall validation message */}
              <div className={`
                p-3 rounded-lg border-2 text-sm
                ${areAllTotalsValid() 
                  ? 'bg-green-50 border-green-300 text-green-800' 
                  : 'bg-red-50 border-red-300 text-red-800'
                }
              `}>
                {areAllTotalsValid() ? (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    All cruise type percentages are valid (total 100% each)
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">⚠</span>
                    Each cruise type row must total exactly 100%
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Connect AI Assistant (ChatGPT) — optional, read-only integration.
            Available regardless of the selected tab. */}
        <div className="border-t border-gray-200 mt-6 pt-6">
          <AssistantConnectSection />
        </div>

        {/* Active Sessions / device management — sits at the bottom of the
            settings dialog, available regardless of which tab is currently
            selected so users can always reach "Sign out other devices". */}
        <div className="border-t border-gray-200 mt-6 pt-6">
          <ActiveSessionsPanel />
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading || !areAllTotalsValid()}
            className="bg-sky-500 hover:bg-sky-600"
          >
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserSettingsDialog;
